import {
  ComputedProductStatus,
  InventoryByLotStatus,
  InventoryLotStatus,
  InventoryStatus,
  ProductTestStatus,
} from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

// Controls visibility of UI elements for actions not yet implemented
export const SHOULD_SHOW_UNIMPLEMENTED = false

export const PRODUCT_STATUS_OPTIONS: Option<ComputedProductStatus>[] = [
  { value: ComputedProductStatus.Active, label: "Active" },
  { value: ComputedProductStatus.Inactive, label: "Inactive" },
]

export const INVENTORY_STATUS_TO_LABEL: Record<InventoryStatus, string> = {
  [InventoryStatus.Good]: "Good",
  [InventoryStatus.Low]: "Low",
  [InventoryStatus.Out]: "Out",
  [InventoryStatus.OnHold]: "On Hold",
} as const

export const INVENTORY_STATUS_OPTIONS: Option<InventoryStatus>[] = Object.values(InventoryStatus).map((status) => ({
  value: status,
  label: INVENTORY_STATUS_TO_LABEL[status],
}))

export const ALL_WAREHOUSES_VALUE = "ALL"

export const ALL_WAREHOUSES_OPTION: Option<string> = { value: ALL_WAREHOUSES_VALUE, label: "All Warehouses" }

export const INVENTORY_LOT_STATUS_TO_LABEL: Record<InventoryByLotStatus, string> = {
  [InventoryByLotStatus.Approved]: "Approved",
  [InventoryByLotStatus.DirectShip]: "Direct Ship",
  [InventoryByLotStatus.NotEvaluated]: "Not Evaluated",
  [InventoryByLotStatus.OnHold]: "On Hold",
  [InventoryByLotStatus.Pending]: "Pending",
  [InventoryByLotStatus.Rejected]: "Rejected",
} as const

// Pending is left out: the system sets it while AS400 has not acknowledged the change, so users cannot select it.
export const INVENTORY_LOT_ELIGIBLE_QUALITY_STATUSES = [
  ProductTestStatus.Approved,
  ProductTestStatus.OnHold,
  ProductTestStatus.DirectShip,
  ProductTestStatus.Rejected,
] as const

export type InventoryLotEligibleQualityStatus = (typeof INVENTORY_LOT_ELIGIBLE_QUALITY_STATUSES)[number]

export const INVENTORY_LOT_QUALITY_STATUS_TO_LABEL: Record<InventoryLotEligibleQualityStatus, string> = {
  [ProductTestStatus.Approved]: "Approved",
  [ProductTestStatus.OnHold]: "On Hold",
  [ProductTestStatus.DirectShip]: "Direct Ship",
  [ProductTestStatus.Rejected]: "Rejected",
}

export const INVENTORY_LOT_QUALITY_STATUS_OPTIONS: Option<InventoryLotEligibleQualityStatus>[] =
  INVENTORY_LOT_ELIGIBLE_QUALITY_STATUSES.map((status) => ({
    value: status,
    label: INVENTORY_LOT_QUALITY_STATUS_TO_LABEL[status],
  }))

// Not Evaluated is left out on purpose: Inventory By Lot reports it both for Direct Ship lots and for lots that are
// missing from the Product Lot Master, so there is no reliable current status to preselect.
export const INVENTORY_LOT_STATUS_TO_QUALITY_STATUS: Partial<
  Record<InventoryByLotStatus, InventoryLotEligibleQualityStatus>
> = {
  [InventoryByLotStatus.Approved]: ProductTestStatus.Approved,
  [InventoryByLotStatus.OnHold]: ProductTestStatus.OnHold,
  [InventoryByLotStatus.DirectShip]: ProductTestStatus.DirectShip,
  [InventoryByLotStatus.Rejected]: ProductTestStatus.Rejected,
}

export const INVENTORY_PALLET_LOT_STATUS_TO_LABEL: Record<InventoryLotStatus, string> = {
  [InventoryLotStatus.Available]: "Available",
  [InventoryLotStatus.PalletHold]: "Pallet Hold",
  [InventoryLotStatus.LotHold]: "Lot Hold",
  [InventoryLotStatus.Allocated]: "Allocated",
  [InventoryLotStatus.PartiallyAllocated]: "Partially Allocated",
} as const

export const PALLET_HOLD_TOOLTIP = "Pallet Hold is an AEROS-only status."

export enum PalletHoldAction {
  Put = "put",
  Release = "release",
}
